import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Filter, PackageMinus, Plus } from 'lucide-react'

import type {
    ListPurchaseNotesParams,
    PurchaseLotOutReason,
} from '@/lib/api/purchase-lot-api'
import type { NoteStatus } from '@/lib/api/sorting-lot-api'
import {
    PurchaseLotStatusBadge,
    purchaseLotOutReasonLabel,
} from '@/components/purchase-lots/purchase-lot-badges'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import {
    NOTE_CARD_CODE_CLASS,
    NoteListCard,
} from '@/components/common/NoteListCard'
import { RefreshButton } from '@/components/common/refresh-button'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useIsMobile } from '@/hooks/use-is-mobile'
import {
    usePurchaseOutNotes,
    usePurchaseOutNotesInfinite,
} from '@/hooks/use-purchase-lot-query'
import { formatDateTimeVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface Search {
    status?: NoteStatus
    reason?: PurchaseLotOutReason
    page?: number
}

const STATUSES = ['pending', 'approved', 'rejected', 'cancelled'] as const
const REASONS = ['for_sorting', 'discard', 'adjustment'] as const

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/purchase-lots/stock-out-notes/',
)({
    component: Page,
    validateSearch: (search: Record<string, unknown>): Search => {
        const out: Search = {}
        if (
            typeof search.status === 'string' &&
            STATUSES.includes(search.status as NoteStatus)
        ) {
            out.status = search.status as NoteStatus
        }
        if (
            typeof search.reason === 'string' &&
            REASONS.includes(search.reason as PurchaseLotOutReason)
        ) {
            out.reason = search.reason as PurchaseLotOutReason
        }
        const page =
            typeof search.page === 'number' ? search.page : Number(search.page)
        if (Number.isFinite(page) && page > 1) out.page = page
        return out
    },
})

function Page() {
    const search = Route.useSearch()
    const navigate = useNavigate({ from: '/purchase-lots/stock-out-notes/' })
    const setSearch = (next: Search) =>
        void navigate({ search: () => next, replace: false })
    const params = useMemo<ListPurchaseNotesParams>(
        () => ({
            page: search.page ?? 1,
            per_page: 50,
            status: search.status,
            reason: search.reason,
        }),
        [search],
    )
    const isMobile = useIsMobile()
    const { data, isLoading, isFetching, refetch } = usePurchaseOutNotes(
        params,
        { enabled: !isMobile },
    )

    // Mobile: infinite (params bỏ page — hook tự quản)
    const infiniteParams = useMemo(
        () => ({
            per_page: 50,
            ...(search.status ? { status: search.status } : {}),
            ...(search.reason ? { reason: search.reason } : {}),
        }),
        [search.status, search.reason],
    )
    const infinite = usePurchaseOutNotesInfinite(infiniteParams, {
        enabled: isMobile,
    })
    const mobileItems = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )

    const notes = data?.data ?? []
    const filterActive =
        !!search.status || !!search.reason || (search.page ?? 1) > 1
    const clearFilter = () => setSearch({})

    const [filterDialogOpen, setFilterDialogOpen] = useState(false)

    const renderFilters = () => (
        <>
            <div>
                <Label className="mb-1 block">
                    {m.sl_note_filter_status()}
                </Label>
                <Select
                    value={search.status ?? 'all'}
                    onValueChange={(value) =>
                        setSearch({
                            ...search,
                            status:
                                value === 'all'
                                    ? undefined
                                    : (value as NoteStatus),
                            page: undefined,
                        })
                    }
                >
                    <SelectTrigger className="w-full">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.sl_note_filter_all_status()}
                        </SelectItem>
                        <SelectItem value="pending">
                            {m.pl_status_pending()}
                        </SelectItem>
                        <SelectItem value="approved">
                            {m.pl_status_approved()}
                        </SelectItem>
                        <SelectItem value="rejected">
                            {m.pl_status_rejected()}
                        </SelectItem>
                        <SelectItem value="cancelled">
                            {m.pl_status_cancelled()}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label className="mb-1 block">
                    {m.sl_note_filter_reason()}
                </Label>
                <Select
                    value={search.reason ?? 'all'}
                    onValueChange={(value) =>
                        setSearch({
                            ...search,
                            reason:
                                value === 'all'
                                    ? undefined
                                    : (value as PurchaseLotOutReason),
                            page: undefined,
                        })
                    }
                >
                    <SelectTrigger className="w-full">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.pl_note_filter_all_reasons()}
                        </SelectItem>
                        {REASONS.map((reason) => (
                            <SelectItem key={reason} value={reason}>
                                {purchaseLotOutReasonLabel(reason)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </>
    )

    return (
        <div className="space-y-4 p-4 md:p-6">
            <ListPageHeader
                icon={<PackageMinus className="size-5 shrink-0" />}
                title={m.pl_pxk_title()}
                utilityActions={
                    <>
                        <RefreshButton
                            onClick={() => void refetch()}
                            loading={isFetching}
                        />
                        <ClearFilterButton
                            active={filterActive}
                            onClear={clearFilter}
                        />
                        <Button
                            variant="outline"
                            size="icon"
                            className="md:hidden"
                            onClick={() => setFilterDialogOpen(true)}
                        >
                            <Filter className="size-4" />
                        </Button>
                    </>
                }
                primaryActions={
                    <Button asChild>
                        <Link
                            to="/purchase-lots/stock-out-notes/new"
                            className="w-full"
                        >
                            <Plus className="size-4 mr-1" />
                            {m.pl_pxk_create()}
                        </Link>
                    </Button>
                }
            />

            <div className="hidden md:grid gap-3 md:grid-cols-2">
                {renderFilters()}
            </div>

            <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
                <DialogContent className="max-w-none w-screen h-[100svh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-none p-4 sm:h-auto sm:w-full sm:max-w-lg sm:rounded-xl sm:p-6">
                    <DialogHeader>
                        <DialogTitle>{m.pl_pxk_title()}</DialogTitle>
                    </DialogHeader>
                    <div className="grid content-start gap-3 overflow-y-auto py-2">
                        {renderFilters()}
                    </div>
                    <DialogFooter className="flex-row gap-2 pt-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="h-11 flex-1"
                            onClick={() => {
                                clearFilter()
                                setFilterDialogOpen(false)
                            }}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            className="h-11 flex-1"
                            onClick={() => setFilterDialogOpen(false)}
                        >
                            {m.common_filter_apply()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.pl_lots_loading()}
                    </div>
                ) : mobileItems.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.pl_note_empty()}
                    </div>
                ) : (
                    <>
                        {mobileItems.map((note) => (
                            <NoteListCard
                                key={note.id}
                                onClick={() =>
                                    void navigate({
                                        to: '/purchase-lots/stock-out-notes/$id',
                                        params: { id: note.id },
                                    })
                                }
                                code={
                                    <Link
                                        to="/purchase-lots/stock-out-notes/$id"
                                        params={{ id: note.id }}
                                        className={NOTE_CARD_CODE_CLASS}
                                    >
                                        {note.code}
                                    </Link>
                                }
                                statusBadge={
                                    <PurchaseLotStatusBadge
                                        status={note.status}
                                    />
                                }
                                reason={purchaseLotOutReasonLabel(note.reason)}
                                totalLots={
                                    note.items_count ?? note.items?.length ?? 0
                                }
                                createdBy={note.created_by_name ?? undefined}
                                createdAt={note.created_at}
                            />
                        ))}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="w-full h-11"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.pl_note_col_code()}</TableHead>
                            <TableHead>{m.pl_note_col_status()}</TableHead>
                            <TableHead>{m.pl_note_col_reason()}</TableHead>
                            <TableHead className="text-center">
                                {m.pl_note_col_items()}
                            </TableHead>
                            <TableHead>{m.pl_note_col_created_by()}</TableHead>
                            <TableHead>{m.pl_note_col_created_at()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.pl_lots_loading()}
                                </TableCell>
                            </TableRow>
                        ) : notes.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.pl_note_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            notes.map((note) => (
                                <TableRow
                                    key={note.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/purchase-lots/stock-out-notes/$id',
                                            params: { id: note.id },
                                        })
                                    }
                                >
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/purchase-lots/stock-out-notes/$id"
                                            params={{ id: note.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {note.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <PurchaseLotStatusBadge
                                            status={note.status}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {purchaseLotOutReasonLabel(note.reason)}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        {note.items_count ??
                                            note.items?.length ??
                                            0}
                                    </TableCell>
                                    <TableCell>
                                        {note.created_by_name ?? '—'}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatDateTimeVN(note.created_at)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}
